import React, { PureComponent, useState, useEffect, createRef } from 'react';
import { find, isEmpty, isNull, uniq, cloneDeep, isNil, isEqual } from 'lodash';
import { LicenseManager } from 'ag-grid-enterprise';
import { titleize } from 'underscore.string';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import ColumnsSetStore from 'stores/ColumnsSetStore';
import ColumnsWebAPIUtils from 'utils/ColumnsWebAPIUtils';
import SessionStore from 'stores/SessionStore';
import FilterActionCreators from 'actions/FilterActionCreators';
import {
  getShipmentsByStream,
  getShipmentsCount,
  getUVCsvExport,
  createReport,
  updateReport,
  getReport,
  deleteReport,
  setFavoriteReport,
} from 'utils/UnifiedViewWebAPIUtils';
import FiltersStore from 'stores/FiltersStore';
import OrderModule from 'components/modules/orders/Module';
import ShipmentModule from 'components/modules/shipments/ShipmentDetailsContainer';
import CargomaticModal from 'components/v2/modal';
import { withTranslation } from 'react-i18next';
import { getLocaleText } from 'utils/localization/agGridLocale';
import { withLocaleContext } from 'providers/locale-settings-context';
import USStates from 'constants/us-states';
import { isUS } from 'helpers/helper_functions';
import { sluggify, firstLetterTitlization } from 'utils/localization/utils';
import { Menu } from '@headlessui/react';
import Panel from 'components/v2/Panel';
import PanelSection from 'components/v2/PanelSection';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import Breadcrumbs from 'components/v2/Breadcrumbs';
import Favorite from './Favorite';
import Layout from './Layout';

import { shipmentHasEquipment, getStopGroupsArray } from './utils';

import BulkActions from './BulkActions';
import OrdersBulkActions from './orders/BulkActions';
import ShipmentExplorer from './ExploreShipmentUnifiedView';
import { getDrayageColumns, getShorthaulColumns } from './columns';
import SaveFilterModal from './SaveFilterModal';
import { IMPORT, DOMESTIC } from '../../../config/order-types';
import {
  STANDARD_REFERENCE_NUMBERS,
  SIDEBAR_STATUSES,
  TIME_SELECTIONS,
} from './constants';

import {
  tagFormatter,
  statusFormatter,
  portIntegrationsFormatter,
  orderIDRenderer,
  shipmentIDRenderer,
  marginFormatter,
  deliveryOrderFormatter,
  checkedFormatter,
  lineSeparatedStringFormatter,
} from './formatters';
import TextFilterWithExclude from './customFilters/TextFilterWithExclude';
import TextFilter from './customFilters/TextFilter';
import SaveModal from './SaveModal';
import DeleteReportModal from './DeleteReportModal';
import {
  getModelParams,
  transformFilterModelQuery,
  getMinValidDate,
} from './transformers';
import FilterChips from './FilterChips';
import { withPageContext } from './PageContext';
import { withGroups } from '../../../hooks/unifiedview/useReports';
import Alert from 'components/ui/Alert';
import { checkShorthaulAppointmentNeeded } from 'helpers/shipments';
import {
  coalesceItems,
  coalesceLegacyAndNewFields,
} from 'utils/coalesceLegacyAndNewFields';
import coalesceColumnDefs from 'utils/coalesceColumnDefinitions';
import { showToast } from 'stores/ToastStore';
import OrderExplorer from './orders/Explore';

const ENTITY_ORDER = 'order';
const ENTITY_SHIPMENT = 'shipment';

const getOptions = (t) => [
  {
    name: t('DRAYAGE'),
    value: 'drayage',
  },
  {
    name: t('SHORTHAUL'),
    value: 'shorthaul',
  },
];

LicenseManager.setLicenseKey(`${AG_GRID_LICENSE}`);

const ARRIVED_ORDER_TYPES = [IMPORT, DOMESTIC];
const INACTIVE_SHIPMENT_STATUS = ['Job Closed', 'Canceled'];

export const DEFAULT_CREATED_AT_SORT = {
  colId: 'created_at',
  sort: 'desc',
  sortIndex: 99,
};

export const perPageOptions = [50, 100];

const drayageFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'drayage',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'rail',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const shorthaulFilterModel = {
  shipment_type: {
    condition1: {
      type: 'equals',
      filter: 'shorthaul',
      filterType: 'text',
    },
    condition2: {
      type: 'equals',
      filter: 'longhaul',
      filterType: 'text',
    },
    condition3: {
      type: 'equals',
      filter: 'dayrate',
      filterType: 'text',
    },
    operator: 'OR',
  },
};

const sidebarParams = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: false,
        suppressValues: false,
        suppressPivots: true,
        suppressPivotMode: true,
        suppressColumnFilter: false,
        suppressColumnSelectAll: false,
        suppressColumnExpandAll: false,
        suppressSyncLayoutWithGrid: true,
      },
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
      toolPanelParams: {
        suppressExpandAll: false,
        suppressFilterSearch: false,
      },
    },
  ],
};

const FilterListComponent = (props) => {
  return (
    <div data-testid="div-deb3fcfd">
      <fieldset data-testid="fieldset-27f5ee24">
        <span data-testid="span-1a7741ac" className="form-label">
          {props.name}
        </span>
        {props.filters.length === 0 && (
          <div data-testid="div-11dd6ab9" className="text-sm text-grey-dark">
            No Filters Found...
          </div>
        )}
        <div data-testid="div-dcce3944" className="flex">
          {props.filters.map((filter, i) => {
            return (
              <div data-testid={`div-18634715-${i}`} className="mr-2" key={i}>
                <span
                  data-testid={`span-61cabd6a-${i}`}
                  className="mr-2 text-sm text-grey-darker"
                >
                  {filter.name}
                </span>
                <input
                  data-testid={`input-07f6fb46-${i}`}
                  key={filter._id}
                  type="checkbox"
                  value=""
                  checked={props.selectedFilter._id === filter._id}
                  onChange={(e) => {
                    props.onFilterChange(
                      filter,
                      props.selectedFilter,
                      props.filterSelected,
                      e.target.value,
                    );
                  }}
                />
              </div>
            );
          })}
        </div>
      </fieldset>
    </div>
  );
};

FilterListComponent.propTypes = {
  name: PropTypes.string,
  filters: PropTypes.array,
  selectedFilter: PropTypes.object,
};

// A preliminary component for redirecting users from areas they do not have access too.
const CountDown = () => {
  const [count, setCount] = useState(3);

  useEffect(() => {
    const interval = setInterval(() => {
      if (count > 0) {
        setCount(count - 1);
      }
    }, 1000);

    // Cleanup the interval when the component unmounts
    return () => clearInterval(interval);
  }, [count]);

  if (count === 0) {
    window.location.href = '/';
  }

  return (
    <div
      data-testid="div-9b2113eb"
      className="py-32 flex w-full justify-center align-middle"
    >
      <div
        data-testid="div-d4c2ae09"
        className="flex flex-col justify-center align-middle"
      >
        <div data-testid="div-e4259329" className="flex justify-center py-2">
          <i
            data-testid="i-148a5ea3"
            className="fa fa-spinner fa-spin text-5xl text-green"
          />
        </div>
        <h4 data-testid="h4-925412f4">
          Access not allowed. Redirecting in {count} seconds...
        </h4>
      </div>
    </div>
  );
};

class MasterList extends PureComponent {
  isApplyingFilters = false;

  constructor(props) {
    super(props);
    this.gridRef = createRef();
    this.localeText = getLocaleText();

    this.state = {
      gridReady: false,
      unifiedViewV2: props.unifiedViewV2,
      breadcrumbs: { pages: [] },
      group: null,
      isSavingNewReport: false,
      showConfirmDeleteReportModal: false,
      showSaveModal: false,
      showSaveNewModal: false,
      sidebarState: 'expanded',
      columnsChanged: false,
      applyingSavedFilters: false,
      showDateInfoAlert: true,
      filterModel: {},
      stopGroupStats: {},
      userAllowed: SessionStore.canAccessUnifiedView(), // For now this is limited to admin users
      changeCarrierDisabled: true,
      changeShipperDisabled: true,
      arrivedActionDisabled: true,
      columnDefs: this.getColumnDefs('drayage', props.t),
      view: 'drayage',
      columns: [],
      statusBar: { statusPanels: [{ statusPanel: 'agAggregationComponent' }] },
      frameworkComponents: {
        deliveryOrderFormatter,
        statusFormatter: statusFormatter,
        portIntegrationsFormatter: portIntegrationsFormatter,
        checkedFormatter: checkedFormatter,
        lineSeparatedStringFormatter: lineSeparatedStringFormatter,
        tagFormatter: tagFormatter,
        orderIDRenderer: orderIDRenderer,
        shipmentIDRenderer: shipmentIDRenderer,
        marginFormatter: marginFormatter,
        textFilterWithExclude: TextFilterWithExclude,
        textFilter: TextFilter,
      },
      showSaveFilterModal: false,
      localFilters: [],
      sharedFilters: [],
      filterSelected: false,
      canShare: SessionStore.canShareFilters(),
      shared: false,
      groupDefaultExpanded: 2,
      selectedFilter: {
        name: '',
        id: '',
      },
      order_oid: '',
      showOrderModal: false,
      shipment_oid: '',
      showShipmentModal: false,
      isFirst: true,
      bulkUploadDocumentsDisabled: true,
      isSameShipperOrMasterShipper: false,
      getRowNodeId: (data) => data._id,
      selectedShipments: [],
      selectedShipmentsForAccessorials: [],
      selectedShipmentsForYards: [],
      loadingData: true,
      activePage: 1,
      pages: 1,
      queryPayload: {
        params: {},
        options: {
          viewType: 'intermodal',
          per_page: 100,
          sort: [DEFAULT_CREATED_AT_SORT],
        },
      },
      shouldShowDefaultFilterInfo: true,
      rowCount: 0,
      isFetchingData: false,
    };
  }

  // Stable function reference to avoid triggering grid updates each render
  getRowId = (params) => params.data._id;

  unifiedViewV2Enabled = () => this.props?.unifiedViewV2 === true;

  getColumnDefs(view, t) {
    const columns =
      view === 'drayage'
        ? getDrayageColumns(this.unifiedViewV2Enabled())
        : getShorthaulColumns();
    const processed = columns
      .map((incoming) => {
        const column = { ...incoming };
        column.menuTabs = ['filterMenuTab', 'generalMenuTab'];

        if (column.field === 'created_at') {
          column.filterParams.minValidDate = getMinValidDate();
        }

        if (column.field === 'order_id') {
          column.cellRendererParams = {
            innerRenderer: (props) => {
              return props.value;
            },
            onOrderClick: (data) => {
              this.setState({
                order_oid: data,
                showOrderModal: true,
              });
            },
          };
        } else if (column.field === 'shipment_id') {
          column.cellRendererParams = {
            innerRenderer: (props) => {
              return props.value;
            },
            onShipmentClick: (data) => {
              const { shipment_oid } = data;
              this.setState({
                shipment_oid,
                showShipmentModal: true,
              });
            },
          };
        }

        if (
          isUS() &&
          column.field &&
          column.field.toLowerCase().includes('state')
        ) {
          column.filter = 'agSetColumnFilter';
          column.floatingFilter = true;
          column.filterParams = {
            values: USStates.map((state) => state.abbreviation),
          };
        }
        return column;
      })
      .map((c) => {
        c.headerName = c.titleize
          ? firstLetterTitlization(t(sluggify(c.headerName)))
          : t(sluggify(c.headerName));
        c.headerTooltip = t(sluggify(c.headerTooltip));
        return c;
      });

    return this.unifiedViewV2Enabled()
      ? coalesceColumnDefs(processed)
      : processed;
  }

  async componentDidMount() {
    FiltersStore.addChangeListener(this.handleFiltersStoreUpdate);
    FiltersStore.addFiltersChangeListener(this.handleFiltersUpdate);
    ColumnsSetStore.addChangeListener(this.handleColumnUpdate);

    FilterActionCreators.getFilterNames();
    ColumnsWebAPIUtils.getCols();

    document.title = 'Cargomatic Inc., - Command: Unified View';

    this.setState({ unifiedViewV2: this.props.unifiedViewV2 });

    const isExploreView = window.location.pathname.includes('/explore');
    let selectedEntityType = '';
    if (isExploreView) {
      selectedEntityType = window.location.pathname.includes('/orders')
        ? ENTITY_ORDER
        : ENTITY_SHIPMENT;
    }
    if (this.props.match?.params?.reportId) {
      // Defer selectedEntityType until the report loads so the grid doesn't
      // mount until the report's filterModel is in state.
      // handleFetchReport sets selectedEntityType from the report.
      this.setState({ isExploreView });
      await this.handleFetchReport();
    } else {
      this.setState({ isExploreView, selectedEntityType });
    }
    this.loadBreadCrumbs();
  }

  componentDidUpdate(_prevProps, prevState) {
    if (prevState.report !== this.state.report) {
      this.loadBreadCrumbs();
    }
  }

  UNSAFE_componentWillReceiveProps = (nextProps) => {
    if (nextProps.unifiedViewV2 !== this.props.unifiedViewV2) {
      this.setState({ unifiedViewV2: nextProps.unifiedViewV2 }, () => {
        if (!isNil(this.state.unifiedViewV2)) {
          // Abort any in-flight requests before reloading under new LD flag
          this.abortControllers();
        }
      });
    }
  };

  fetchData = () => {
    this.getFilterResults(false, true);
  };

  abortControllers() {
    // Count and queries abort controllers.
    if (this.abortCountController) {
      if (!this.abortCountController.signal.aborted) {
        this.abortCountController.abort();
      }
    }
    if (this.abortQueryController) {
      if (!this.abortQueryController.signal.aborted) {
        this.abortQueryController.abort();
      }
    }
  }

  componentWillUnmount() {
    FiltersStore.removeChangeListener(this.handleFiltersStoreUpdate);
    FiltersStore.removeFiltersChangeListener(this.handleFiltersUpdate);
    ColumnsSetStore.removeChangeListener(this.handleColumnUpdate);
    document.title = 'Cargomatic Inc. - Command';
  }

  handleUpdateRows = (updates) => {
    const map = updates.reduce((map, update) => {
      map[update._id] = update;
      return map;
    }, {});
    this.setState((prevState) => {
      const items = prevState.items.map((item) => {
        if (map[item.shipment_oid]) {
          return {
            ...item,
            ...map[item.shipment_oid],
          };
        }
        return item;
      });
      return { items };
    });
  };

  handleToggleFavorite = async () => {
    if (!this.state.report) {
      return;
    }

    const isFavorite = !this.state.report.isFavorite;

    const { isSuccess } = await setFavoriteReport({
      id: this.state.report._id,
      isFavorite,
    });

    if (!isSuccess) {
      return;
    }

    this.setState((prevState) => ({
      report: {
        ...prevState.report,
        isFavorite,
      },
    }));
  };

  handleColumnUpdate = (update) => {
    const { columns, event } = update;
    if (event === 'CREATE') {
      this.setState(
        {
          columns,
        },
        () => {
          // this.reset();
        },
      );
    } else {
      this.setState({
        columns,
      });
    }
  };

  onHideShipmentModal = () => {
    this.setState({
      showShipmentModal: false,
    });
  };

  onHideModal = () => {
    this.setState({
      showOrderModal: false,
    });
  };

  handleFetchReport = async () => {
    const {
      match: { params },
      t,
    } = this.props;

    // Fallback entity type from URL
    const fallbackEntityType = window.location.pathname.includes('/orders')
      ? ENTITY_ORDER
      : ENTITY_SHIPMENT;

    try {
      const report = await getReport({
        queryKey: ['getReport', params.reportId],
      });

      this.setState({
        report,
        selectedEntityType: report.filterType || fallbackEntityType,
        filterModel: report.model || {},
        view: report.view || 'drayage',
      });
    } catch (_e) {
      // Still mount the grid so the user isn't stuck on a blank page
      this.setState({ selectedEntityType: fallbackEntityType });
      showToast(t('ERROR_FETCHING_REPORT'), null, 'error');
    }
  };

  onEditingStopped = (event) => {
    if (!event.valueChanged || !event.colDef.updateFunc) {
      return;
    }

    event.colDef.updateFunc(event);
  };

  onFilterChange = (filterModel) => {
    if (this.state.isQuickFiltered) return;
    if (isEqual(this.state.filterModel, filterModel)) return;

    // The first event after report load is from async set filter loading —
    // the grid model is incomplete (missing async keys like marketCode).
    // Mark filterChanged but don't sync filterModel or fetch.
    // After this, filterChanged is true and user changes proceed normally.

    this.setState({ filterModel }, () => {
      if (this.state.selectedEntityType === ENTITY_SHIPMENT) {
        this.handlePagination(1);
      }
    });
  };

  onSortChanged = () => {
    if (this.state.isQuickFiltered) return;
    if (this.state.applyingSavedFilters) return;

    this.getFilterResults();
  };

  handleFiltersUpdate = (update) => {
    const { filters, event } = update;
    const localFilters = filters
      .filter((filter) => filter.shared !== true)
      .sort((a, b) => (a?.name?.trim() || '').localeCompare(b.name.trim()));
    const sharedFilters = filters
      .filter((filter) => filter.shared === true)
      .sort((a, b) => (a?.name?.trim() || '').localeCompare(b?.name?.trim()));
    if (event === 'CREATED') {
      const {
        result: { shared, _id },
      } = update;
      const cols = [];
      this.gridRef.current?.api.getAllGridColumns().forEach((col) => {
        const { userProvidedColDef, visible } = col;
        if (!isNull(userProvidedColDef)) {
          const { field } = userProvidedColDef;
          if (visible) {
            cols.push(field);
          }
        }
      });
      const filter_id = _id;
      if (!shared) {
        ColumnsWebAPIUtils.createCols({ filter_id, cols });
      }
    }

    this.setState({
      localFilters,
      sharedFilters,
    });
  };

  handleRowDisplayed = () => {
    const rowCount = this.gridRef.current?.api.getDisplayedRowCount();
    if (rowCount !== this.state.rowCount) {
      this.setState({ rowCount });
    }
  };

  handleFiltersStoreUpdate = (row) => {
    const { add, update } = row;

    if (this.state.isFirst) {
      this.gridRef.current?.api.batchUpdateRowData({ add });
      this.setState({
        isFirst: false,
      });
    } else {
      if (add.length > 0) {
        this.gridRef.current?.api.batchUpdateRowData({ add });
        this.handleRowDisplayed();
      }
      if (update.length > 0) {
        this.gridRef.current?.api.batchUpdateRowData({ update });
      }
    }
  };

  getSavedFilterModel = (filter) => {
    // move primaryDate to model to have backward compatibility
    if (filter.primaryDate && filter.timeSelected?.length) {
      // check if model has same column filter we do nothing
      if (!(filter.model && filter.model[filter.primaryDate])) {
        // model can not exist
        filter.model = filter.model || {};
        // set primary
        const timeSelectedType = TIME_SELECTIONS.find(
          (ts) => ts.name === filter.timeSelected,
        )?.localizationSlug;
        if (timeSelectedType) {
          filter.model[filter.primaryDate] = {
            dateFrom: null,
            dateTo: null,
            filterType: 'date',
            type: timeSelectedType,
          };
        }
      }
    }

    if (!filter.model) {
      return {};
    }
    const mappedFilterModel = Object.keys(filter.model).reduce((model, key) => {
      const value = filter.model[key];
      model[key] = value;

      // remove empty filters
      if (Array.isArray(value.value) && value.value.length === 0) {
        delete model[key];
      }

      // remove not used condition to not treat them as new default condition, see: CARGO-7936
      const notUsedConditions = ['contains', 'notContains'];
      // plain filter
      if (notUsedConditions.includes(value.type)) {
        delete model[key];
      }
      // filter with multiple conditions
      const conditions = Object.keys(value).filter((key) =>
        key.startsWith('condition'),
      );
      if (conditions.length) {
        conditions.forEach((conditionKey) => {
          if (notUsedConditions.includes(value[conditionKey].type)) {
            delete model[key];
          }
          // if array of conditions
          if (
            Array.isArray(value[conditionKey]) &&
            value[conditionKey].length
          ) {
            value[conditionKey].forEach((conditionFilter) => {
              if (notUsedConditions.includes(conditionFilter.type)) {
                delete model[key];
              }
            });
          }
        });
      }

      return model;
    }, {});

    return this.unifiedViewV2Enabled()
      ? coalesceLegacyAndNewFields(mappedFilterModel)
      : mappedFilterModel;
  };

  setCustomFilterModels = (savedFilterModel) => {
    if (!this.gridRef.current?.api) return;
    const customFilterColumns = [
      'shipment_id',
      'order_id',
      'orderStatus',
      'chassis_number',
      'shipper_name',
      'reference_numbers',
      ...STANDARD_REFERENCE_NUMBERS,
    ];

    const stopColumnFields = this.state.columnDefs
      .filter((c) => c.children)
      .flatMap((c) => c.children)
      .map((c) => c.field);
    const customFilterStopColumns = stopColumnFields.filter(
      (f) =>
        f &&
        (f.endsWith('stop_equipment_id') || f.endsWith('stop_chassis_number')),
    );

    const customFilterFields = [
      ...customFilterColumns,
      ...customFilterStopColumns,
    ];

    customFilterFields.forEach((field) => {
      const filterInstance = this.gridRef.current?.api.getFilterInstance(field);
      if (!isEmpty(filterInstance)) {
        filterInstance.setModel(
          savedFilterModel && savedFilterModel[field]
            ? savedFilterModel[field]
            : null,
        );
      }
    });
  };

  handleFilterSelect = () => {
    if (
      !this.gridRef.current?.api ||
      this.state.selectedEntityType !== ENTITY_SHIPMENT
    )
      return;
    const { report } = this.state;

    const filter = report ?? {};
    if (filter._id !== this.state.selectedFilter?._id) {
      this.setState(
        (prevState) => {
          const { columns } = prevState;
          const columnSet = find(columns, (col) => {
            return col.filter_id === filter._id;
          });
          const mappedFilterModel = this.getSavedFilterModel(filter);
          return {
            view: filter.view,
            selectedFilter: filter,
            filterSelected: true,
            activeCols: columnSet ? columnSet._id : null,
            applyingSavedFilters: true,
            filterModel: { ...mappedFilterModel },
          };
        },
        () => {
          this.gridRef.current?.api.autoSizeColumns();

          this.fetchData();
        },
      );
    }
  };

  getModelParams(useStateFilterModel = false) {
    const { view } = this.state;
    const filterModel = cloneDeep(
      useStateFilterModel
        ? this.state.filterModel
        : this.gridRef.current?.api.getFilterModel(),
    );

    return getModelParams({
      view,
      filterModel,
      agGridApi: this.gridRef.current?.api,
    });
  }

  exportCsv = async () => {
    // Use the same params from the current query to ensure export matches
    // what the user sees. This ensures all filters (including set filters)
    // are properly included and have already been transformed.
    let params;
    let options;
    if (this.state.selectedEntityType === ENTITY_SHIPMENT) {
      params = this.state.queryPayload.params;
      options = {
        sort: this.getSortModel(),
        viewType: this.state.view === 'drayage' ? 'intermodal' : 'shorthaul',
      };
    } else {
      params = this.gridRef.current?.queryParams();
      options = {
        sort: this.getSortModel(),
        viewType: ENTITY_ORDER,
      };
    }

    const sameDay = moment(this.state.dateTo).isSame(
      moment(this.state.dateFrom, 'day'),
    );

    let columns = this.gridRef.current?.api
      .getAllGridColumns()
      .filter((c) => c.visible && c.colDef.field)
      .map((c) => {
        // BE expecting field for data mapping and actual headerName for csv header
        return {
          field: c.colDef.field,
          headerName: c.colDef.headerName,
        };
      });

    // Apply coalescing to columns if unifiedViewV2 is enabled
    if (this.unifiedViewV2Enabled()) {
      columns = coalesceColumnDefs(columns);
    }

    this.gridRef.current?.api.showLoadingOverlay();
    this.setState({ exportLoading: true }, async () => {
      if (this.state.primaryDate) {
        params[this.state.primaryDate] = {
          dateFrom: this.state.dateFrom,
          dateTo: this.state.dateTo,
          type: sameDay ? 'equals' : 'inRange',
          filterType: 'date',
        };
      }

      // Removed unifiedViewV2Enabled to let function default handle proxy through mono
      await getUVCsvExport({
        params,
        options,
        columns,
      });
      this.gridRef.current?.api.hideOverlay();
      this.setState({ exportLoading: false });
    });
  };

  hideSaveFilterModal = () => {
    this.setState({ showSaveFilterModal: false });
  };

  /**
   * Common function to reset the view with default filters and settings
   * @param {string} view - The view type ('drayage' or 'shorthaul')
   * @param {boolean} resetView - Whether to reset the view type to default
   */
  resetView = (view = 'drayage', resetView = false) => {
    // Clear all current filters
    this.gridRef.current?.api.setFilterModel(
      this.state.report ? this.state.report.model : null,
    );
    this.gridRef.current?.api.setQuickFilter(null);

    // Determine the target view
    const targetView = resetView ? 'drayage' : view;

    // Get appropriate column definitions and filter model for the view
    const columnDefs = this.getColumnDefs(targetView, this.props.t);
    const model =
      targetView === 'drayage' ? drayageFilterModel : shorthaulFilterModel;

    // Create default query payload with proper date filters and view settings
    const queryPayload = {
      options: {
        page: 1,
        per_page: 100,
        sort: [DEFAULT_CREATED_AT_SORT],
        viewType: targetView === 'drayage' ? 'intermodal' : 'shorthaul',
      },
      params: {
        ...model,
      },
    };

    // Reset state with default values
    this.setState(
      {
        filterSelected: false,
        selectedFilter: { id: '', name: '' },
        activeCols: null,
        columnDefs,
        queryPayload,
        activePage: 1,
        filterModel: this.state.report ? this.state.report.model : {},
        view: targetView,
        // Reset selection states
        selectedShipments: [],
        selectedShipmentsForAccessorials: [],
        selectedShipmentsForYards: [],
        changeCarrierDisabled: true,
        changeShipperDisabled: true,
        arrivedActionDisabled: true,
        bulkUploadDocumentsDisabled: true,
        isSameShipperOrMasterShipper: false,
      },
      () => {
        // Apply the reset to the grid
        this.gridRef.current?.api.setColumnDefs(this.state.columnDefs);
        this.gridRef.current?.api.autoSizeColumns();
        this.gridRef.current?.api.resetColumnState();
        this.setCustomFilterModels(null);
        this.gridRef.current?.api.onFilterChanged();

        // Clear any selected rows
        this.gridRef.current?.api.deselectAll();
        this.gridRef.current?.resetView();

        // Trigger data refresh with new filters
        this.getFilterResults();
      },
    );
  };

  reset = () => {
    if (this.state.selectedEntityType === ENTITY_SHIPMENT) {
      this.resetView(this.state.view, false);
    } else {
      this.gridRef.current?.resetView();
    }
  };

  /**
   * Reset to default view (drayage) with all default settings
   */
  resetToDefault = () => {
    if (this.state.selectedEntityType === ENTITY_ORDER) {
      this.gridRef.current?.api?.resetView();
    } else if (this.state.selectedEntityType === ENTITY_SHIPMENT) {
      // Use the common resetView function to reset to default drayage view
      this.resetView('drayage', true);
    }
  };

  getColumnState() {
    const columnState = this.gridRef.current?.api.getColumnState();
    const pinned = columnState
      .filter((c) => c.pinned)
      .map((c) => ({ field: c.colId, pinned: c.pinned }));
    const hidden = columnState.filter((c) => c.hide).map((c) => c.colId);
    const columnOrder = columnState.map((c) => c.colId);

    return { columnOrder, pinned, hidden };
  }

  handleViewChange = (view) => {
    // Use the common resetView function when changing views
    this.resetView(view, false);
  };

  handleEntityTypeChange = (entityType) => {
    this.setState({ selectedEntityType: entityType });
    const newPath =
      entityType === 'order'
        ? '/unifiedview/explore/orders'
        : '/unifiedview/explore';
    window.history.replaceState(null, '', newPath);
  };

  getRowGroupColumns = () => {
    return this.gridRef.current?.api
      .getRowGroupColumns()
      .map((col) => col.colId);
  };

  getColumnsSize() {
    const columnsState = this.gridRef.current?.api.getColumnState();

    return columnsState.map((columnState) => {
      return { field: columnState.colId, size: columnState.width };
    });
  }

  getColumnSorting() {
    const columnsState = this.gridRef.current?.api.getColumnState();

    return columnsState.map((columnState) => {
      return {
        field: columnState.colId,
        sort: columnState.sort,
        sortIndex: columnState.sortIndex,
      };
    });
  }

  saveAs = (name) => {
    const { shared, view } = this.state;
    const model = cloneDeep(this.gridRef.current?.api.getFilterModel());
    const rowGroupColumns = this.getRowGroupColumns();
    const rowColumnsSize = this.getColumnsSize();
    const rowColumnsSorting = this.getColumnSorting();
    const canEdit = false;
    const { columnOrder, pinned, hidden } = this.getColumnState();
    const newFilter = {
      name,
      model,
      rowGroupColumns,
      shared,
      view,
      canEdit,
      pinned,
      hidden,
      columnOrder,
      rowColumnsSize,
      rowColumnsSorting,
    };

    FilterActionCreators.createFilter(newFilter);
    this.hideSaveFilterModal();
  };

  update = (name) => {
    const { selectedFilter, shared, view } = this.state;
    let _id;
    const model = cloneDeep(this.gridRef.current?.api.getFilterModel());
    const rowGroupColumns = this.getRowGroupColumns();
    const rowColumnsSize = this.getColumnsSize();
    const rowColumnsSorting = this.getColumnSorting();
    const { columnOrder, pinned, hidden } = this.getColumnState();
    const updates = {
      id: selectedFilter._id,
      model,
      rowGroupColumns,
      name,
      shared,
      view,
      pinned,
      hidden,
      columnOrder,
      rowColumnsSize,
      rowColumnsSorting,
    };

    FilterActionCreators.updateFilter(updates);

    if (this.state.activeCols) {
      _id = this.state.activeCols;
      const cols = [];
      this.gridRef.current?.api.getAllGridColumns().forEach((col) => {
        const { userProvidedColDef, visible } = col;
        if (!isNull(userProvidedColDef)) {
          const { field } = userProvidedColDef;
          if (visible) {
            cols.push(field);
          }
        }
      });
      if (!shared) {
        ColumnsWebAPIUtils.updateCols({ _id, cols });
      }
    }

    this.hideSaveFilterModal();
  };

  toggleShare = () => {
    const { shared } = this.state;
    this.setState({
      shared: !shared,
    });
  };

  areShorthaulAppointmentNeeded(selectedRows) {
    return selectedRows.some((shipment) => {
      const { shipment_type, shipmentStatus } = shipment;
      return checkShorthaulAppointmentNeeded(
        shipment_type,
        sluggify(shipmentStatus)?.toLowerCase(),
      );
    });
  }

  handleRowSelection = () => {
    const selectedRows = this.gridRef.current?.api?.getSelectedRows() || [];
    let arrivedActionDisabled = true;

    let changeCustomerDisabled = false;
    if (this.state.selectedEntityType === ENTITY_ORDER) {
      this.setState({ selectedOrderRows: selectedRows });
    } else if (this.state.selectedEntityType === ENTITY_SHIPMENT) {
      if (selectedRows.length) {
        arrivedActionDisabled = !selectedRows.every(
          (s) => !!s.order_oid && ARRIVED_ORDER_TYPES.includes(s.order_type),
        );
        changeCustomerDisabled = selectedRows.some((s) =>
          INACTIVE_SHIPMENT_STATUS.includes(s.shipmentStatus),
        );
        const sameShipperOrMasterShipper =
          this.areShipmentsWithSameShipperOrMasterShipper(selectedRows);
        const changeCarrierDisabled =
          !this.areShipmentsWithSameShipper(selectedRows) ||
          this.areShorthaulAppointmentNeeded(selectedRows);
        const bulkUploadDocumentsDisabled = !sameShipperOrMasterShipper;
        const selectedIds = uniq(selectedRows.map((s) => s.shipment_oid));

        const selectedShipmentsForAccessorials = uniq(
          selectedRows
            .filter(
              (s) => s.shipment_type === 'drayage' && shipmentHasEquipment(s),
            )
            .map((shipment) => {
              const {
                shipment_oid: _id,
                shipment_id,
                order_oid: orderId,
              } = shipment;

              return {
                ...shipment,
                _id,
                shipment_id,
                stops: getStopGroupsArray(shipment),
                orderId,
              };
            }),
        );

        const selectedShipmentsForYards = uniq(
          selectedRows
            .filter(
              (s) => s.shipment_type === 'drayage' && shipmentHasEquipment(s),
            )
            .map((shipment) => {
              // get equipment ids from stop groups
              const stopGroups = getStopGroupsArray(shipment);

              const mappedStopEquipments = stopGroups.flatMap((s) => {
                const equipments =
                  s.stop_equipment_oid !== '' ? [s.stop_equipment_id] : [];
                return equipments;
              });

              // distinct
              const equipments =
                mappedStopEquipments?.length > 0
                  ? [...new Set(mappedStopEquipments)]
                  : [];

              return {
                id: shipment.shipment_oid,
                equipments: equipments,
                shipment_id: shipment.shipment_id,
                market_id: shipment.market_code,
              };
            }),
        );

        this.setState({
          arrivedActionDisabled,
          changeCarrierDisabled:
            changeCustomerDisabled || changeCarrierDisabled,
          changeShipperDisabled: changeCustomerDisabled,
          bulkUploadDocumentsDisabled,
          isSameShipperOrMasterShipper: sameShipperOrMasterShipper,
          selectedShipments: selectedIds,
          selectedShipmentsForAccessorials,
          selectedShipmentsForYards,
        });
      }
    } else {
      this.setState({
        arrivedActionDisabled,
        changeCarrierDisabled: changeCustomerDisabled,
        changeShipperDisabled: changeCustomerDisabled,
        selectedShipments: [],
        selectedShipmentsForAccessorials: [],
        selectedShipmentsForYards: [],
      });
    }
  };

  getDataTotal = () => {
    const { userAllowed } = this.state;
    if (!userAllowed) {
      return;
    }

    this.setState(async (prevState) => {
      this.abortCountController = new AbortController();

      // Removed unifiedViewV2Enabled to let function default handle proxy through mono
      const totalCount = await getShipmentsCount(
        {
          params: {
            ...prevState.queryPayload.params,
          },
          options: {
            viewType: prevState.view === 'drayage' ? 'intermodal' : 'shorthaul',
          },
        },
        { controller: this.abortCountController },
      );
      const pages = Math.ceil(
        totalCount / prevState.queryPayload.options.per_page,
      );

      this.setState({ totalCount, pages });
    });
  };

  getSortModel = () => {
    return this.gridRef.current?.api
      .getColumnState()
      .filter((col) => !!col.sort);
  };

  getSortModelInsteadOfDefaultSort = () => {
    return this.gridRef.current?.api
      .getColumnState()
      .filter(
        (col) =>
          !!col.sort && col.sortIndex < DEFAULT_CREATED_AT_SORT.sortIndex,
      );
  };

  transformFilterModelQuery(useStateFilterModel = false) {
    const modelParams = this.getModelParams(useStateFilterModel);

    return transformFilterModelQuery(modelParams);
  }

  isCreatedAtSortApplied = (columnsState) => {
    return columnsState.find(
      (item) =>
        item.colId === DEFAULT_CREATED_AT_SORT.colId &&
        item.sortIndex !== DEFAULT_CREATED_AT_SORT.sortIndex &&
        item.sortIndex !== null,
    );
  };

  getFilterResults = (isPolling = false, useStateFilterModel = false) => {
    // When a saved report is initially loading, use the state filter model —
    // ag-grid async set filters (e.g. marketCode) may not be reflected in
    // the grid model yet.  Once the user changes filters (filterChanged),
    // fall back to the grid model as the source of truth.
    if (this.state.report && !this.state.filterChanged) {
      useStateFilterModel = true;
    }
    const {
      userAllowed,
      activePage: page,
      queryPayload: {
        options: { per_page },
      },
    } = this.state;

    this.setState({ isFetchingData: true }, async () => {
      if (!userAllowed) {
        return;
      }

      if (this.gridRef.current?.api) {
        if (!isPolling) {
          this.gridRef.current?.api.showLoadingOverlay();
        }

        // make the query string for the shared/pesonal filters
        let params = this.transformFilterModelQuery(useStateFilterModel);

        const options = {
          sort: this.getSortModelInsteadOfDefaultSort(),
          per_page,
          page,
          viewType: this.state.view === 'drayage' ? 'intermodal' : 'shorthaul',
        };

        const columnsState = this.gridRef.current?.api.getColumnState();

        if (!this.isCreatedAtSortApplied(columnsState)) {
          const createdAtSort = columnsState.find(
            (col) => col.colId === DEFAULT_CREATED_AT_SORT.colId,
          );
          if (createdAtSort) {
            createdAtSort.sort = DEFAULT_CREATED_AT_SORT.sort;
            createdAtSort.sortIndex = DEFAULT_CREATED_AT_SORT.sortIndex;
            options.sort.push({ ...createdAtSort });
          }
        }

        this.setState(
          (previousState) => {
            return {
              ...previousState,
              loadingData: true,
              activePage: page || 1,
              queryPayload: {
                ...previousState.queryPayload,
                params,
                options,
              },
            };
          },
          async () => {
            // Any pending requests should abort.
            this.abortControllers();

            this.abortQueryController = new AbortController();

            // Get Count
            this.getDataTotal();

            // Get Query Results
            // Removed unifiedViewV2Enabled to let function default handle proxy through mono
            const data = await getShipmentsByStream(
              {
                params,
                options,
              },
              {
                controller: this.abortQueryController,
              },
            );
            this.handleDataStream(data);
            this.setState({ isFetchingData: false });
          },
        );
      }
    });
  };

  handleDataStream = async (data) => {
    try {
      const { items: incomingItems } = data;

      const items = this.unifiedViewV2Enabled()
        ? coalesceItems(incomingItems)
        : incomingItems;

      this.setState(() => {
        const updates = {
          items,
          loadingData: false,
          applyingSavedFilters: false,
        };

        return updates;
      });
    } catch (e) {
      console.error(e);
    }
  };

  handlePerPageChange = (per_page) => {
    this.setState(
      (prevState) => ({
        activePage: 1,
        queryPayload: {
          ...prevState.queryPayload,
          options: {
            ...prevState.queryPayload.options,
            per_page: parseInt(per_page, 10),
          },
        },
      }),
      () => this.getFilterResults(),
    );
  };

  handlePagination = (page = 1) => {
    const { activePage, isFetchingData } = this.state;
    if (page === activePage && isFetchingData) return;
    this.setState({ activePage: page }, () => {
      this.getFilterResults();
    });
  };

  areShipmentsWithSameShipper = (selectedRows) => {
    if (selectedRows.length === 1) {
      return true;
    }
    return (
      selectedRows[0]?.shipper_oid &&
      selectedRows.every((shipment) => {
        return shipment.shipper_oid === selectedRows[0].shipper_oid;
      })
    );
  };

  areShipmentsWithSameShipperOrMasterShipper = (selectedRows) => {
    if (selectedRows.length === 1) {
      return true;
    }
    const sameShipper =
      selectedRows[0]?.shipper_oid &&
      selectedRows.every((shipment) => {
        return shipment?.shipper_oid === selectedRows[0]?.shipper_oid;
      });
    const sameMasterShipper =
      selectedRows[0]?.master_account_id &&
      selectedRows.every((shipment) => {
        return (
          shipment?.master_account_id === selectedRows[0]?.master_account_id
        );
      });

    return sameShipper || sameMasterShipper;
  };

  handleShowSaveReportModal = () => {
    this.setState({ showSaveModal: true, isSavingNewReport: false });
  };

  handleHideSaveReportModal = () => {
    this.setState({ showSaveModal: false });
  };

  handleShowSaveNewReportModal = () => {
    this.setState({ showSaveModal: true, isSavingNewReport: true });
  };

  handleShowConfirmDeleteReportModal = () => {
    this.setState({ showConfirmDeleteReportModal: true });
  };

  handleHideConfirmDeleteReportModal = () => {
    this.setState({ showConfirmDeleteReportModal: false });
  };

  handleConfirmDelete = async () => {
    const { report } = this.state;
    if (!report) {
      return;
    }
    await deleteReport({ ...report, id: report._id });
    this.handleHideConfirmDeleteReportModal();
    this.props.history.push('/unifiedview/explore', { shallow: true });
    this.setState({ report: null });
  };

  handleSaveReport = async (report) => {
    const id = report?.id;
    this.handleHideSaveReportModal();

    const { view, selectedEntityType } = this.state;
    const model = this.state.filterModel;
    const rowGroupColumns = this.getRowGroupColumns();
    const rowColumnsSize = this.getColumnsSize();
    const rowColumnsSorting = this.getColumnSorting();
    const canEdit = false;
    const { columnOrder, pinned, hidden } = this.getColumnState();
    let reportToSave = report;

    if (id) {
      const updatedReport = {
        model,
        rowGroupColumns,
        view,
        pinned,
        hidden,
        columnOrder,
        rowColumnsSize,
        rowColumnsSorting,
        ...report,
      };
      reportToSave = await updateReport(updatedReport);
    } else {
      const newReport = {
        model,
        rowGroupColumns,
        view,
        canEdit,
        pinned,
        hidden,
        columnOrder,
        rowColumnsSize,
        rowColumnsSorting,
        filterType: selectedEntityType,
        ...report,
      };
      reportToSave = await createReport(newReport);

      window.history.replaceState(
        null,
        '',
        `/unifiedview/reports/${reportToSave._id}`,
      );
    }

    this.setState({ report: reportToSave });
  };

  removeFilter = (filterName) => {
    const filterModel = { ...this.state.filterModel };
    delete filterModel[filterName];

    // Suppress onFilterChange during programmatic filter update —
    // onFilterChanged() dispatches the filterChanged event synchronously.
    this.isApplyingFilters = true;
    try {
      this.gridRef.current?.api.setFilterModel(filterModel);
      this.gridRef.current?.api.onFilterChanged();
    } catch (e) {
      console.error('removeFilter: grid update failed:', e);
    } finally {
      this.isApplyingFilters = false;
    }

    this.setState({ filterModel }, () => {
      if (this.state.selectedEntityType === ENTITY_SHIPMENT) {
        this.fetchData();
      }
    });
  };

  loadBreadCrumbs() {
    const { t, pageContext, groups } = this.props;

    const { updatePage } = pageContext || {};
    if (this.state.report) {
      const { report } = this.state;
      const pages = [];
      let group;

      if (report.groupId) group = groups.getGroupById(report.groupId);

      if (pageContext.previousPage) {
        pages.push({
          name: pageContext.previousPageName,
          href: pageContext.previousPage,
        });

        if (pageContext.previousPageName === group?.name) {
          let currentGroup = group;
          while (currentGroup?.parent) {
            const parentGroup = groups.getGroupById(currentGroup.parent);
            pages.unshift({
              name: parentGroup.name,
              href: `/unifiedview/groups/${parentGroup._id}/reports`,
            });
            currentGroup = parentGroup;
          }
        }
      } else if (group) {
        pages.push({
          name: group.name,
          href: `/unifiedview/groups/${report.groupId}/reports`,
        });

        let currentGroup = group;
        while (currentGroup?.parent) {
          const parentGroup = groups.getGroupById(currentGroup.parent);
          pages.unshift({
            name: parentGroup.name,
            href: `/unifiedview/groups/${parentGroup._id}/reports`,
          });
          currentGroup = parentGroup;
        }
      }

      const breadcrumbs = {
        homeUrl: '/unifiedview/reports',
        onClick: updatePage,
        pages: [
          ...pages,
          {
            name: report.name,
            href: `/unifiedview`,
            current: true,
            render: () => {
              return (
                <div
                  data-testid="div-ae8c4f22"
                  className="flex items-center space-x-2"
                >
                  <span data-testid="span-ede2cd20">{report.name}</span>
                  <DropdownMenu dataTestId="DropdownMenu-c7f46423" useIcon>
                    {this.state.report && (
                      <Menu.Item
                        data-testid="MenuItem-3e12d060"
                        key="save_report"
                      >
                        <DropdownItem
                          useIcon
                          dataTestId="updateReportButton"
                          onClick={this.handleShowSaveReportModal}
                          disabled={
                            !SessionStore.isAbleToManageUVReport(report)
                          }
                          disabledText={t(
                            'YOU_HAVE_NO_PERMISSIONS_TO_UPDATE_REPORT',
                          )}
                        >
                          {t('UPDATE_REPORT')}
                        </DropdownItem>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      data-testid="MenuItem-9e4e87bb"
                      key="save_new_report"
                    >
                      <DropdownItem
                        useIcon
                        dataTestId="saveNewReportButton"
                        onClick={this.handleShowSaveNewReportModal}
                      >
                        {t('SAVE_NEW_REPORT')}
                      </DropdownItem>
                    </Menu.Item>
                    {this.state.report && (
                      <Menu.Item
                        data-testid="MenuItem-dad3bdaa"
                        key="delete_rate"
                      >
                        <DropdownItem
                          dataTestId="deleteReportButton"
                          onClick={this.handleShowConfirmDeleteReportModal}
                          disabled={
                            !SessionStore.isAbleToManageUVReport(report)
                          }
                          disabledText={t(
                            'YOU_HAVE_NO_PERMISSIONS_TO_DELETE_REPORT',
                          )}
                        >
                          {t('DELETE_REPORT')}
                        </DropdownItem>
                      </Menu.Item>
                    )}
                    <Menu.Item
                      data-testid="MenuItem-70fc7fdc"
                      key="export_report"
                    >
                      <DropdownItem
                        useIcon
                        dataTestId="exportReportButton"
                        onClick={this.exportCsv}
                      >
                        {t('EXPORT_TO_CSV')}
                      </DropdownItem>
                    </Menu.Item>
                  </DropdownMenu>
                </div>
              );
            },
          },
        ],
      };
      this.setState({ breadcrumbs });

      return;
    }
    const breadcrumbs = {
      homeUrl: '/unifiedview/reports',
      onClick: updatePage,
      pages: [
        {
          name: 'Explore',
          href: `/unifiedview/explore`,
          current: true,
          render: () => {
            return (
              <div
                data-testid="div-50c7a082"
                className="flex items-center space-x-2"
              >
                <span data-testid="span-7e48723e">{t('EXPLORE')}</span>
                <DropdownMenu dataTestId="DropdownMenu-b4609936" useIcon>
                  <Menu.Item
                    data-testid="MenuItem-34e9993e"
                    key="save_new_report"
                  >
                    <DropdownItem
                      useIcon
                      dataTestId="saveNewReportButton"
                      onClick={this.handleShowSaveNewReportModal}
                    >
                      {t('SAVE_NEW_REPORT')}
                    </DropdownItem>
                  </Menu.Item>
                  <Menu.Item
                    data-testid="MenuItem-639b165e"
                    key="export_report"
                  >
                    <DropdownItem
                      useIcon
                      dataTestId="exportReportButton"
                      onClick={this.exportCsv}
                    >
                      {t('EXPORT_TO_CSV')}
                    </DropdownItem>
                  </Menu.Item>
                </DropdownMenu>
              </div>
            );
          },
        },
      ],
    };
    this.setState({ breadcrumbs });
  }

  render() {
    const { userAllowed } = this.state;
    if (!userAllowed) {
      return <CountDown />;
    }

    const { t } = this.props;

    const { match } = this.props;
    const params = match?.params;
    const groupId = this.state.report?.groupId;

    const activeTab = groupId ? 'reports' : 'explore';

    const isSavedReport = !!params?.reportId;

    return (
      <Layout
        groupId={groupId}
        activeTab={activeTab}
        sidebarDefaultState={SIDEBAR_STATUSES.COLLAPSED}
      >
        <div data-testid="div-d75254a9" className="px-4 sm:px-6 lg:px-8">
          <div data-testid="div-84559398" className="">
            <div data-testid="div-27313d28" className="flex space-x-4">
              <Breadcrumbs
                dataTestId="Breadcrumbs-ea92556d"
                breadcrumbs={this.state.breadcrumbs}
              />
              {this.state.report && (
                <Favorite
                  selected={this.state.report?.isFavorite}
                  onToggle={this.handleToggleFavorite}
                />
              )}
            </div>
            <Panel dataTestId="Panel-1084a244">
              <PanelSection dataTestId="PanelSection-fe125a89">
                <div
                  data-testid="div-43cab497"
                  className="flex justify-between flex-wrap items-center space-x-4"
                >
                  <FilterChips
                    className="flex-1"
                    columnDefs={this.state.columnDefs}
                    filterModel={this.state.filterModel}
                    onRemove={this.removeFilter}
                    view={this.state.view}
                  />
                  <div
                    data-testid="div-d4fc9b45"
                    className="flex space-x-4 items-center ml-auto"
                  >
                    <a
                      data-testid="a-5708e095"
                      href="javascript:void(0)"
                      className="w-20 block"
                      onClick={this.reset}
                    >
                      {t('CLEAR_FILTERS')}
                    </a>
                    {!isSavedReport && (
                      <DropdownMenu
                        dataTestId="selectShipmentType"
                        title={
                          getOptions(t).find(
                            (option) => option.value === this.state.view,
                          )?.name || t('SELECT_VIEW')
                        }
                      >
                        {getOptions(t).map((option) => (
                          <Menu.Item key={option.value}>
                            <DropdownItem
                              dataTestId={`selectShipmentType-SelectItem-${option.value}`}
                              onClick={() =>
                                this.handleViewChange(option.value)
                              }
                              disabled={
                                option.value === 'shorthaul' &&
                                this.state.selectedEntityType === 'order'
                              }
                            >
                              {option.name}
                            </DropdownItem>
                          </Menu.Item>
                        ))}
                      </DropdownMenu>
                    )}
                    {this.props.enableOrdersUnifiedView &&
                      this.state.isExploreView &&
                      this.state.view === 'drayage' && (
                        <DropdownMenu
                          dataTestId="DropdownMenu-shipment-order"
                          title={
                            this.state.selectedEntityType === 'shipment'
                              ? t('SHIPMENT')
                              : t('ORDER')
                          }
                        >
                          <Menu.Item
                            data-testid="MenuItem-shipment"
                            key="shipment"
                          >
                            <DropdownItem
                              dataTestId="shipmentOption"
                              onClick={() =>
                                this.handleEntityTypeChange('shipment')
                              }
                            >
                              {t('SHIPMENT')}
                            </DropdownItem>
                          </Menu.Item>
                          <Menu.Item data-testid="MenuItem-order" key="order">
                            <DropdownItem
                              dataTestId="orderOption"
                              onClick={() =>
                                this.handleEntityTypeChange('order')
                              }
                            >
                              {t('ORDER')}
                            </DropdownItem>
                          </Menu.Item>
                        </DropdownMenu>
                      )}
                    {this.state.selectedEntityType === ENTITY_SHIPMENT && (
                      <BulkActions
                        view={this.state.view}
                        disabled={this.state.selectedShipments.length === 0}
                        changeCarrierDisabled={this.state.changeCarrierDisabled}
                        changeShipperDisabled={this.state.changeShipperDisabled}
                        arrivedActionDisabled={this.state.arrivedActionDisabled}
                        bulkUploadDocumentsDisabled={
                          this.state.bulkUploadDocumentsDisabled
                        }
                        isSameShipperOrMasterShipper={
                          this.state.isSameShipperOrMasterShipper
                        }
                        selectedShipments={this.state.selectedShipments}
                        selectedRows={
                          this.gridRef.current?.getSelectedRows() || []
                        }
                        selectedShipmentsForYards={
                          this.state.selectedShipmentsForYards
                        }
                        selectedShipmentsForAccessorials={
                          this.state.selectedShipmentsForAccessorials
                        }
                        onBillingStatusPendingReviewUpdate={
                          this.handleUpdateRows
                        }
                      />
                    )}
                    {this.state.selectedEntityType === ENTITY_ORDER && (
                      <OrdersBulkActions
                        disabled={
                          (this.state.selectedOrderRows || []).length === 0
                        }
                        selectedOrders={this.state.selectedOrderRows}
                      />
                    )}
                  </div>
                </div>
                {this.state.exportLoading && (
                  <Alert
                    className="mt-2"
                    type="warning"
                    title={t('UV_EXPORT_ALERT_TITLE')}
                  >
                    {t('UV_EXPORT_ALERT_MSG')}
                  </Alert>
                )}
              </PanelSection>
              <div className="mt-4 py-4 ag-theme-balham h-[90vh]">
                {this.state.selectedEntityType === ENTITY_SHIPMENT && (
                  <ShipmentExplorer
                    ref={this.gridRef}
                    items={this.state.items}
                    getRowId={this.getRowId}
                    components={this.state.frameworkComponents}
                    statusBar={this.state.statusBar}
                    columnDefs={this.state.columnDefs}
                    onSortChanged={this.onSortChanged}
                    onSelectionChanged={this.handleRowSelection}
                    onRowDisplayed={this.handleRowDisplayed}
                    stopGroupStats={this.state.stopGroupStats}
                    onFilterChange={this.onFilterChange}
                    onEditingStopped={this.onEditingStopped}
                    report={this.state.report}
                    fetchData={this.fetchData}
                    localeText={this.localeText}
                    loadingText={t('LOADING')}
                    sidebarParams={sidebarParams}
                    paginationTotal={this.state.totalCount}
                    paginationPage={this.state.activePage}
                    paginationPerPage={this.state.queryPayload.options.per_page}
                    paginationPerPageOptions={perPageOptions}
                    onPageClick={(page) => this.handlePagination(page)}
                    onPerPageChange={(value) => this.handlePerPageChange(value)}
                    handlePagination={this.handlePagination}
                    handleFilterSelect={this.handleFilterSelect}
                    unifiedViewV2Enabled={this.unifiedViewV2Enabled()}
                  />
                )}
                {this.state.selectedEntityType === ENTITY_ORDER && (
                  <OrderExplorer
                    ref={this.gridRef}
                    onSelectionChanged={this.handleRowSelection}
                    onFilterChange={this.onFilterChange}
                    report={this.state.report}
                    sidebarParams={sidebarParams}
                    unifiedViewV2Enabled={this.unifiedViewV2Enabled()}
                  />
                )}
              </div>
            </Panel>
          </div>

          {this.state.showSaveFilterModal && (
            <SaveFilterModal
              filterSelected={this.state.filterSelected}
              selectedFilter={this.state.selectedFilter}
              onUpdate={this.update}
              setShareWithTeam={this.toggleShare}
              share={this.state.shared}
              onSave={this.saveAs}
              show
              onHide={this.hideSaveFilterModal}
              canShareWithTeam={this.state.canShare}
            />
          )}
          {this.state.showOrderModal && (
            <CargomaticModal
              dataTestId="CargomaticModal-226ea836"
              show
              location="top"
              title={titleize(t('DETAIL', { count: 2 }))}
              onHide={this.onHideModal}
              body={<OrderModule id={this.state.order_oid} renderedInModal />}
              footer={null}
              dialogClassName="w-11/12"
            />
          )}
          {this.state.showShipmentModal && (
            <CargomaticModal
              dataTestId="CargomaticModal-82d68cc1"
              className="w-full"
              show
              onHide={this.onHideShipmentModal}
              title={titleize(t('DETAIL', { count: 2 }))}
              body={<ShipmentModule modalID={this.state.shipment_oid} />}
              footer={null}
              location="top"
              dialogClassName="w-11/12"
            />
          )}
        </div>
        <SaveModal
          isOpen={this.state.showSaveModal}
          onClose={this.handleHideSaveReportModal}
          isNew={this.state.isSavingNewReport}
          onSave={this.handleSaveReport}
          report={this.state.report}
        />
        <DeleteReportModal
          isOpen={this.state.showConfirmDeleteReportModal}
          onClose={this.handleHideConfirmDeleteReportModal}
          onConfirm={this.handleConfirmDelete}
        />
      </Layout>
    );
  }
}

export default withTranslation()(
  withLocaleContext(withPageContext(withGroups(MasterList))),
);
